"use client";

import * as React from "react";

import { GridTee } from "@/components/ui/grid-connector";
import { cn } from "@/lib/utils";

export interface ChapterDefinition {
  id: string;
  label: string;
}

interface ChapterNavProps {
  chapters: ChapterDefinition[];
  className?: string;
}

/** Sub-pixel tolerance so a section counts as reached at its exact landing
 *  position (getBoundingClientRect and scrollY both carry fractions). */
const ACTIVATION_TOLERANCE = 1;

/** Scroll-margin-top a section reserves for the fixed nav and sticky chapter
 *  bar — i.e. where an anchor jump actually parks it. */
function getScrollMarginTop(el: HTMLElement): number {
  const value = Number.parseFloat(window.getComputedStyle(el).scrollMarginTop);
  return Number.isFinite(value) ? value : 0;
}

export function ChapterNav({ chapters, className }: ChapterNavProps) {
  const [activeId, setActiveId] = React.useState<string | null>(
    chapters[0]?.id ?? null
  );
  const fillRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});
  const navRef = React.useRef<HTMLElement>(null);
  const tabRefs = React.useRef<Record<string, HTMLAnchorElement | null>>({});
  const frameRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const compute = () => {
      frameRef.current = null;
      const viewportTop = window.scrollY;
      const maxScroll = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight
      );

      // A chapter's landing position is the scroll offset an anchor jump parks
      // it at: its top minus its own scroll-margin (fixed nav + sticky chapter
      // bar + breathing room). Both the active tab and the fill bar are keyed
      // off it, so the bar completes exactly as the next tab takes over — using
      // two different reference lines left a gap the bar never rendered.
      const landings = chapters.flatMap((chapter) => {
        const el = document.getElementById(chapter.id);
        if (!el) return [];
        const rect = el.getBoundingClientRect();
        return [
          {
            id: chapter.id,
            height: rect.height || 1,
            landing: rect.top + viewportTop - getScrollMarginTop(el),
          },
        ];
      });

      let nextActive = chapters[0]?.id ?? null;

      landings.forEach((chapter, index) => {
        // Progress runs from this chapter's landing position to the next one's.
        // The last chapter has no successor, so it runs to its own height,
        // clamped to how far the page can actually scroll.
        const next = landings[index + 1];
        const end = Math.min(
          next ? next.landing : chapter.landing + chapter.height,
          maxScroll
        );
        const span = Math.max(1, end - chapter.landing);
        const fill = Math.max(
          0,
          Math.min(1, (viewportTop - chapter.landing) / span)
        );

        const bar = fillRefs.current[chapter.id];
        if (bar) {
          bar.style.transform = `scaleX(${fill})`;
        }

        if (chapter.landing <= viewportTop + ACTIVATION_TOLERANCE) {
          nextActive = chapter.id;
        }
      });

      if (nextActive) {
        setActiveId((prev) => (prev === nextActive ? prev : nextActive));
      }
    };

    const schedule = () => {
      if (frameRef.current !== null) return;
      frameRef.current = window.requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, [chapters]);

  const handleClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Mobile: the bar is a horizontal scroll row (tabs overflow the viewport), so
  // keep the active tab scrolled into view as the page scrolls through sections.
  React.useEffect(() => {
    if (!activeId) return;
    const tab = tabRefs.current[activeId];
    const nav = navRef.current;
    if (!tab || !nav) return;
    // Scroll the tab row only — scrollIntoView would also scroll the page
    // vertically to reveal the nav (visible as a jump on initial load).
    nav.scrollTo({
      left: tab.offsetLeft - (nav.clientWidth - tab.clientWidth) / 2,
    });
  }, [activeId]);

  return (
    <div className="relative">
      {/* Rail top corners — 7px tee centred on each vertical border (Figma
          4492:46624). Moved from the application-process section rail. */}
      <GridTee className="absolute -top-[0.5px] -left-[3.5px] z-10 hidden lg:block" />
      <GridTee className="absolute -top-[0.5px] -right-[3.5px] z-10 hidden lg:block" />
      <nav
        ref={navRef}
        data-home-chapter-nav
        aria-label="Page chapters"
        className={cn(
          // Mobile (Figma 4081:11847): white bar, 24px padding all sides (80px
          // total), 12px gaps, top+bottom hairline. Desktop (4492:46624): 12px pad.
          "bg-grey-0 border-grey-100 flex w-full gap-3 overflow-x-auto border p-6 [-ms-overflow-style:none] [scrollbar-width:none] lg:overflow-x-visible lg:border-t-0 lg:p-3 [&::-webkit-scrollbar]:hidden",
          className
        )}
      >
        {chapters.map((chapter) => {
          const isActive = activeId === chapter.id;
          return (
            <a
              key={chapter.id}
              ref={(el) => {
                tabRefs.current[chapter.id] = el;
              }}
              href={`#${chapter.id}`}
              onClick={handleClick(chapter.id)}
              aria-current={isActive ? "true" : undefined}
              className={cn(
                // Figma tab: h-32, px-12, rounded-8, 12px label.
                "rounded-ds-m relative flex h-8 shrink-0 items-center overflow-hidden px-3 text-[12px] leading-[1.4] tracking-[-0.02em] whitespace-nowrap lg:flex-1 lg:px-2",
                isActive ? "bg-[#f3f3f3]" : "bg-[#f9f9f9] hover:bg-[#f3f3f3]"
              )}
            >
              {isActive && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 left-0 w-full origin-left bg-[#e5e6e6]"
                  ref={(el) => {
                    fillRefs.current[chapter.id] = el;
                  }}
                  style={{ transform: "scaleX(0)", willChange: "transform" }}
                />
              )}
              <span className="text-grey-950 relative">{chapter.label}</span>
            </a>
          );
        })}
      </nav>
    </div>
  );
}
